/**
 * EPIC283-A synthetic fixtures for the labeled discovery prototype.
 * Not live product proof — doubles only.
 */

import {
  type CustomerNounCandidate,
  type DisplayStateKey,
  attentionBoundaryCopy,
  comparabilityLabel,
  customerNounLabel,
  displayStateContent,
  formatDisplayMessage,
  proofSuccessLanguage,
  setupBoundaryCopy,
} from './contentContract';

export type PrototypeViewId = 'now' | 'over_time' | 'since_control';

export type ComprehensionAnswers = {
  displayStateAndAsOf: string;
  topIssueLocation: string;
  sufficientlyMeasured: string;
  material30DayStory: string;
  topActionOwner: string;
  controlResultOrWhyUnavailable: string;
};

export type ViewPayload = {
  viewId: PrototypeViewId;
  title: string;
  siteLabel: string;
  shiftLabel: string;
  asOf: string;
  displayState: DisplayStateKey;
  stateLabel: string;
  message: string;
  limitation: string;
  primaryCta: string;
  issueNoun: string;
  topIssue: string;
  locationLabel: string;
  detailLine: string;
};

export type PrototypeFixture = {
  id: string;
  label: string;
  roleHint: string;
  nounCandidate: CustomerNounCandidate;
  attentionCount: number;
  views: Record<PrototypeViewId, ViewPayload>;
  attention: ReturnType<typeof attentionBoundaryCopy>;
  setup: ReturnType<typeof setupBoundaryCopy>;
  comprehension: ComprehensionAnswers;
};

const SITE = 'Sample warehouse (synthetic)';
const SHIFT = 'Day shift';

function buildView(
  viewId: PrototypeViewId,
  state: DisplayStateKey,
  asOf: string,
  noun: CustomerNounCandidate,
  topIssue: string,
  locationLabel: string,
  detailLine: string,
): ViewPayload {
  const block = displayStateContent(state);
  return {
    viewId,
    title: viewId === 'now' ? 'Now' : viewId === 'over_time' ? 'Over time' : 'Since control',
    siteLabel: SITE,
    shiftLabel: SHIFT,
    asOf,
    displayState: state,
    stateLabel: block.label,
    message: formatDisplayMessage(state, asOf),
    limitation: block.limitation,
    primaryCta: block.primaryCta,
    issueNoun: customerNounLabel(noun),
    topIssue,
    locationLabel,
    detailLine,
  };
}

type ViewSeed = {
  state: DisplayStateKey;
  asOf: string;
  topIssue: string;
  location: string;
  detail: string;
};

function fixture(
  partial: {
    id: string;
    label: string;
    roleHint: string;
    nounCandidate: CustomerNounCandidate;
    attentionCount: number;
    comprehension: ComprehensionAnswers;
    now: ViewSeed;
    overTime: ViewSeed;
    sinceControl: ViewSeed;
  },
): PrototypeFixture {
  const noun = partial.nounCandidate;
  return {
    id: partial.id,
    label: partial.label,
    roleHint: partial.roleHint,
    nounCandidate: noun,
    attentionCount: partial.attentionCount,
    comprehension: partial.comprehension,
    attention: attentionBoundaryCopy(partial.attentionCount),
    setup: setupBoundaryCopy(),
    views: {
      now: buildView(
        'now',
        partial.now.state,
        partial.now.asOf,
        noun,
        partial.now.topIssue,
        partial.now.location,
        partial.now.detail,
      ),
      over_time: buildView(
        'over_time',
        partial.overTime.state,
        partial.overTime.asOf,
        noun,
        partial.overTime.topIssue,
        partial.overTime.location,
        partial.overTime.detail,
      ),
      since_control: buildView(
        'since_control',
        partial.sinceControl.state,
        partial.sinceControl.asOf,
        noun,
        partial.sinceControl.topIssue,
        partial.sinceControl.location,
        partial.sinceControl.detail,
      ),
    },
  };
}

/** Required fixture ids for contract tests / Brief acceptance. */
export const REQUIRED_FIXTURE_IDS = [
  'fresh_current',
  'current_with_limitations',
  'delayed',
  'stale',
  'never_measured',
  'scan_only_collecting',
  'day_0_cold_start',
  'day_7_partial',
  'day_30_story',
  'since_control_valid',
  'since_control_invalid',
  'since_control_inconclusive',
  'proof_complete',
  'empty',
  'blocked',
  'unauthorized',
  'failure',
] as const;

export const PROTOTYPE_FIXTURES: PrototypeFixture[] = [
  fixture({
    id: 'fresh_current',
    label: 'Fresh — Current',
    roleHint: 'safety_leader',
    nounCandidate: 'safety_issue',
    attentionCount: 2,
    now: {
      state: 'current',
      asOf: '2026-08-01 14:05',
      topIssue: 'Pedestrian–vehicle interaction',
      location: 'Aisle B',
      detail: 'Two items need a decision this shift.',
    },
    overTime: {
      state: 'current',
      asOf: '30-day window through 2026-08-01',
      topIssue: 'Pedestrian–vehicle interaction',
      location: 'Aisle B',
      detail: 'Recurring pattern across the last 30 days.',
    },
    sinceControl: {
      state: 'current',
      asOf: 'After mirror install · 2026-07-15',
      topIssue: 'Pedestrian–vehicle interaction',
      location: 'Aisle B',
      detail: `${comparabilityLabel('valid')}. ${proofSuccessLanguage()}.`,
    },
    comprehension: {
      displayStateAndAsOf: 'Current · Updated through 2026-08-01 14:05',
      topIssueLocation: 'Pedestrian–vehicle interaction · Aisle B',
      sufficientlyMeasured: 'Yes — current with no material limitations',
      material30DayStory: 'Recurring pedestrian–vehicle interaction in Aisle B',
      topActionOwner: 'Inspect attention · safety leader',
      controlResultOrWhyUnavailable: proofSuccessLanguage(),
    },
  }),
  fixture({
    id: 'current_with_limitations',
    label: 'Current with limitations',
    roleHint: 'safety_leader',
    nounCandidate: 'recurring_condition',
    attentionCount: 1,
    now: {
      state: 'current_with_limitations',
      asOf: '2026-08-01 13:40',
      topIssue: 'Blocked egress path',
      location: 'Dock 3',
      detail: 'Coverage gap on Camera Dock-West.',
    },
    overTime: {
      state: 'current_with_limitations',
      asOf: '30-day window through 2026-08-01',
      topIssue: 'Blocked egress path',
      location: 'Dock 3',
      detail: 'Limited coverage on several shifts.',
    },
    sinceControl: {
      state: 'current_with_limitations',
      asOf: 'After aisle marking · 2026-07-20',
      topIssue: 'Blocked egress path',
      location: 'Dock 3',
      detail: `${comparabilityLabel('limited')} — coverage differs pre/post.`,
    },
    comprehension: {
      displayStateAndAsOf: 'Current with limitations · 2026-08-01 13:40',
      topIssueLocation: 'Blocked egress path · Dock 3',
      sufficientlyMeasured: 'Partial — coverage limitation on Dock-West',
      material30DayStory: 'Recurring blocked egress near Dock 3',
      topActionOwner: 'View limitation · site admin',
      controlResultOrWhyUnavailable: comparabilityLabel('limited'),
    },
  }),
  fixture({
    id: 'delayed',
    label: 'Delayed updates',
    roleHint: 'safety_leader',
    nounCandidate: 'safety_issue',
    attentionCount: 1,
    now: {
      state: 'delayed',
      asOf: '2026-08-01 11:00',
      topIssue: 'Speed near pedestrian crossing',
      location: 'Crossing C',
      detail: 'Declared cadence placeholder (defer to 283-B).',
    },
    overTime: {
      state: 'delayed',
      asOf: '30-day window (latest usable 2026-08-01 11:00)',
      topIssue: 'Speed near pedestrian crossing',
      location: 'Crossing C',
      detail: 'Trend uses last usable measurements only.',
    },
    sinceControl: {
      state: 'delayed',
      asOf: 'Control dated 2026-07-10',
      topIssue: 'Speed near pedestrian crossing',
      location: 'Crossing C',
      detail: 'Comparison paused while updates are delayed.',
    },
    comprehension: {
      displayStateAndAsOf: 'Delayed · latest measurement 2026-08-01 11:00',
      topIssueLocation: 'Speed near pedestrian crossing · Crossing C',
      sufficientlyMeasured: 'No — updates are delayed',
      material30DayStory: 'Pattern visible but not current',
      topActionOwner: 'View status · ops',
      controlResultOrWhyUnavailable: 'Comparison paused while updates are delayed',
    },
  }),
  fixture({
    id: 'stale',
    label: 'Stale',
    roleHint: 'safety_leader',
    nounCandidate: 'safety_issue',
    attentionCount: 0,
    now: {
      state: 'stale',
      asOf: '2026-07-28 16:00',
      topIssue: 'Unknown — last known issue',
      location: 'Zone North',
      detail: 'Do not treat prior issues as closed.',
    },
    overTime: {
      state: 'stale',
      asOf: 'Last measured 2026-07-28 16:00',
      topIssue: 'Unknown — last known issue',
      location: 'Zone North',
      detail: '30-day story unavailable while stale.',
    },
    sinceControl: {
      state: 'stale',
      asOf: 'Last measured 2026-07-28 16:00',
      topIssue: 'Unknown — last known issue',
      location: 'Zone North',
      detail: 'Since-control comparison unavailable while stale.',
    },
    comprehension: {
      displayStateAndAsOf: 'Stale · last measured 2026-07-28 16:00',
      topIssueLocation: 'Unknown · Zone North',
      sufficientlyMeasured: 'No — current activity unknown',
      material30DayStory: 'Unavailable while stale',
      topActionOwner: 'Restore source · site admin',
      controlResultOrWhyUnavailable: 'Unavailable while stale',
    },
  }),
  fixture({
    id: 'never_measured',
    label: 'Never measured',
    roleHint: 'site_admin',
    nounCandidate: 'recurring_condition',
    attentionCount: 0,
    now: {
      state: 'never_measured',
      asOf: '—',
      topIssue: 'None yet',
      location: 'Whole site',
      detail: 'Complete activation before Now can be sold as current.',
    },
    overTime: {
      state: 'never_measured',
      asOf: '—',
      topIssue: 'None yet',
      location: 'Whole site',
      detail: 'No longitudinal history.',
    },
    sinceControl: {
      state: 'never_measured',
      asOf: '—',
      topIssue: 'None yet',
      location: 'Whole site',
      detail: 'No control comparison without measurement.',
    },
    comprehension: {
      displayStateAndAsOf: 'Never measured · no as-of',
      topIssueLocation: 'None yet · Whole site',
      sufficientlyMeasured: 'No — measurement has not started',
      material30DayStory: 'None',
      topActionOwner: 'Complete activation · site admin + Edgentik support',
      controlResultOrWhyUnavailable: 'Unavailable — never measured',
    },
  }),
  fixture({
    id: 'scan_only_collecting',
    label: 'Scan-only collecting',
    roleHint: 'safety_leader',
    nounCandidate: 'safety_issue',
    attentionCount: 1,
    now: {
      state: 'scan_only_collecting',
      asOf: '2026-08-01 09:30',
      topIssue: 'Forklift near pedestrian path',
      location: 'Aisle D',
      detail: 'Delayed relative to an incremental current view.',
    },
    overTime: {
      state: 'scan_only_collecting',
      asOf: 'Completed scans through 2026-08-01 09:30',
      topIssue: 'Forklift near pedestrian path',
      location: 'Aisle D',
      detail: '30-day view uses completed scans only.',
    },
    sinceControl: {
      state: 'scan_only_collecting',
      asOf: 'Last completed scan 2026-08-01 09:30',
      topIssue: 'Forklift near pedestrian path',
      location: 'Aisle D',
      detail: 'Wait for scan completion before refreshing comparison.',
    },
    comprehension: {
      displayStateAndAsOf: 'Scan collecting · latest completed 2026-08-01 09:30',
      topIssueLocation: 'Forklift near pedestrian path · Aisle D',
      sufficientlyMeasured: 'Partial — waiting on current scan',
      material30DayStory: 'Completed-scan history only',
      topActionOwner: 'View scan · ops',
      controlResultOrWhyUnavailable: 'Wait for scan completion',
    },
  }),
  fixture({
    id: 'day_0_cold_start',
    label: 'Cold start — day 0',
    roleHint: 'safety_leader',
    nounCandidate: 'safety_issue',
    attentionCount: 0,
    now: {
      state: 'current_with_limitations',
      asOf: '2026-08-01 08:00',
      topIssue: 'First observations arriving',
      location: 'Receiving',
      detail: 'Honest empty history — no fabricated trend.',
    },
    overTime: {
      state: 'empty',
      asOf: 'Day 0 of 30-day window',
      topIssue: 'No history yet',
      location: 'Receiving',
      detail: 'Over-time story starts after enough completed days.',
    },
    sinceControl: {
      state: 'blocked',
      asOf: '—',
      topIssue: 'No control baseline',
      location: 'Receiving',
      detail: 'Since control unavailable on day 0.',
    },
    comprehension: {
      displayStateAndAsOf: 'Current with limitations · day 0',
      topIssueLocation: 'First observations · Receiving',
      sufficientlyMeasured: 'Early — limited history',
      material30DayStory: 'None yet (day 0)',
      topActionOwner: 'Continue measurement · site admin',
      controlResultOrWhyUnavailable: 'Unavailable on day 0',
    },
  }),
  fixture({
    id: 'day_7_partial',
    label: 'Cold start — day 7',
    roleHint: 'safety_leader',
    nounCandidate: 'recurring_condition',
    attentionCount: 1,
    now: {
      state: 'current',
      asOf: '2026-08-01 15:00',
      topIssue: 'Staging congestion',
      location: 'Staging pad',
      detail: 'Seven days of measurement available.',
    },
    overTime: {
      state: 'current_with_limitations',
      asOf: '7 of 30 days eligible',
      topIssue: 'Staging congestion',
      location: 'Staging pad',
      detail: 'Partial 30-day window — do not invent missing days.',
    },
    sinceControl: {
      state: 'current_with_limitations',
      asOf: 'Control week incomplete',
      topIssue: 'Staging congestion',
      location: 'Staging pad',
      detail: `${comparabilityLabel('limited')} — short post-control window.`,
    },
    comprehension: {
      displayStateAndAsOf: 'Current · day 7 partial over-time',
      topIssueLocation: 'Staging congestion · Staging pad',
      sufficientlyMeasured: 'Partial — 7 of 30 days',
      material30DayStory: 'Emerging staging congestion (partial window)',
      topActionOwner: 'Inspect attention · supervisor',
      controlResultOrWhyUnavailable: comparabilityLabel('limited'),
    },
  }),
  fixture({
    id: 'day_30_story',
    label: 'Day 30 material story',
    roleHint: 'safety_leader',
    nounCandidate: 'safety_issue',
    attentionCount: 3,
    now: {
      state: 'current',
      asOf: '2026-08-01 16:20',
      topIssue: 'Blind-corner near-miss pattern',
      location: 'Corner 7',
      detail: 'Full 30-day default window eligible.',
    },
    overTime: {
      state: 'current',
      asOf: '30-day window through 2026-08-01',
      topIssue: 'Blind-corner near-miss pattern',
      location: 'Corner 7',
      detail: 'Material 30-day story: rising concentration at Corner 7.',
    },
    sinceControl: {
      state: 'current',
      asOf: 'Mirror + paint · 2026-07-01',
      topIssue: 'Blind-corner near-miss pattern',
      location: 'Corner 7',
      detail: `${comparabilityLabel('valid')}. ${proofSuccessLanguage()}.`,
    },
    comprehension: {
      displayStateAndAsOf: 'Current · 2026-08-01 16:20',
      topIssueLocation: 'Blind-corner near-miss pattern · Corner 7',
      sufficientlyMeasured: 'Yes — 30-day window eligible',
      material30DayStory: 'Rising concentration at Corner 7',
      topActionOwner: 'Inspect attention · safety leader',
      controlResultOrWhyUnavailable: proofSuccessLanguage(),
    },
  }),
  fixture({
    id: 'since_control_valid',
    label: 'Since control — valid comparison',
    roleHint: 'safety_leader',
    nounCandidate: 'safety_issue',
    attentionCount: 1,
    now: {
      state: 'current',
      asOf: '2026-08-01 12:00',
      topIssue: 'Dock crossing exposure',
      location: 'Dock 1',
      detail: 'Post-control window is current.',
    },
    overTime: {
      state: 'current',
      asOf: '30-day window through 2026-08-01',
      topIssue: 'Dock crossing exposure',
      location: 'Dock 1',
      detail: 'Pre/post periods both populated.',
    },
    sinceControl: {
      state: 'current',
      asOf: 'Guardrail install · 2026-07-05',
      topIssue: 'Dock crossing exposure',
      location: 'Dock 1',
      detail: `${comparabilityLabel('valid')}. ${proofSuccessLanguage()}.`,
    },
    comprehension: {
      displayStateAndAsOf: 'Current · valid since-control comparison',
      topIssueLocation: 'Dock crossing exposure · Dock 1',
      sufficientlyMeasured: 'Yes',
      material30DayStory: 'Dock crossing exposure over 30 days',
      topActionOwner: 'Inspect attention · safety leader',
      controlResultOrWhyUnavailable: proofSuccessLanguage(),
    },
  }),
  fixture({
    id: 'since_control_invalid',
    label: 'Since control — cannot compare',
    roleHint: 'safety_leader',
    nounCandidate: 'recurring_condition',
    attentionCount: 1,
    now: {
      state: 'current',
      asOf: '2026-08-01 12:00',
      topIssue: 'Yard pedestrian path',
      location: 'Yard gate',
      detail: 'Measurement basis changed mid-window.',
    },
    overTime: {
      state: 'current_with_limitations',
      asOf: '30-day window with basis change',
      topIssue: 'Yard pedestrian path',
      location: 'Yard gate',
      detail: 'Trend limited after method change.',
    },
    sinceControl: {
      state: 'current_with_limitations',
      asOf: 'Control · 2026-07-12',
      topIssue: 'Yard pedestrian path',
      location: 'Yard gate',
      detail: `${comparabilityLabel('cannot_compare')} — measurement baseline changed.`,
    },
    comprehension: {
      displayStateAndAsOf: 'Current · cannot compare since control',
      topIssueLocation: 'Yard pedestrian path · Yard gate',
      sufficientlyMeasured: 'Limited after baseline change',
      material30DayStory: 'Trend limited by method change',
      topActionOwner: 'View limitation · measurement owner',
      controlResultOrWhyUnavailable: comparabilityLabel('cannot_compare'),
    },
  }),
  fixture({
    id: 'since_control_inconclusive',
    label: 'Since control — inconclusive',
    roleHint: 'safety_leader',
    nounCandidate: 'safety_issue',
    attentionCount: 0,
    now: {
      state: 'current',
      asOf: '2026-08-01 10:00',
      topIssue: 'Rack-end interaction',
      location: 'Rack row 4',
      detail: 'Proof cycle finished without a directional change claim.',
    },
    overTime: {
      state: 'current',
      asOf: '30-day window through 2026-08-01',
      topIssue: 'Rack-end interaction',
      location: 'Rack row 4',
      detail: 'Activity present; change direction unclear.',
    },
    sinceControl: {
      state: 'current',
      asOf: 'Signage refresh · 2026-07-08',
      topIssue: 'Rack-end interaction',
      location: 'Rack row 4',
      detail: comparabilityLabel('inconclusive'),
    },
    comprehension: {
      displayStateAndAsOf: 'Current · inconclusive since control',
      topIssueLocation: 'Rack-end interaction · Rack row 4',
      sufficientlyMeasured: 'Yes',
      material30DayStory: 'Rack-end interactions continue',
      topActionOwner: 'Close proof cycle · safety leader',
      controlResultOrWhyUnavailable: comparabilityLabel('inconclusive'),
    },
  }),
  fixture({
    id: 'proof_complete',
    label: 'Proof cycle complete',
    roleHint: 'safety_leader',
    nounCandidate: 'safety_issue',
    attentionCount: 0,
    now: {
      state: 'current',
      asOf: '2026-08-01 11:30',
      topIssue: 'Intersection conflict',
      location: 'Intersection 2',
      detail: 'Proof narrative complete for selected control.',
    },
    overTime: {
      state: 'current',
      asOf: '30-day window through 2026-08-01',
      topIssue: 'Intersection conflict',
      location: 'Intersection 2',
      detail: 'Pre/post windows complete.',
    },
    sinceControl: {
      state: 'current',
      asOf: 'Stop-bar + mirror · 2026-06-20',
      topIssue: 'Intersection conflict',
      location: 'Intersection 2',
      detail: `Proof complete. ${proofSuccessLanguage()}.`,
    },
    comprehension: {
      displayStateAndAsOf: 'Current · proof complete',
      topIssueLocation: 'Intersection conflict · Intersection 2',
      sufficientlyMeasured: 'Yes',
      material30DayStory: 'Intersection conflict reduced under comparable conditions',
      topActionOwner: 'Archive proof · safety leader',
      controlResultOrWhyUnavailable: proofSuccessLanguage(),
    },
  }),
  fixture({
    id: 'empty',
    label: 'Empty scope',
    roleHint: 'safety_leader',
    nounCandidate: 'recurring_condition',
    attentionCount: 0,
    now: {
      state: 'empty',
      asOf: '2026-08-01 14:00',
      topIssue: 'None',
      location: 'Whole site',
      detail: 'No qualifying activity observed — not a “safe” claim.',
    },
    overTime: {
      state: 'empty',
      asOf: '30-day window through 2026-08-01',
      topIssue: 'None',
      location: 'Whole site',
      detail: 'No qualifying pattern in the window.',
    },
    sinceControl: {
      state: 'empty',
      asOf: '—',
      topIssue: 'None',
      location: 'Whole site',
      detail: 'No control comparison without a selected issue.',
    },
    comprehension: {
      displayStateAndAsOf: 'Empty · 2026-08-01 14:00',
      topIssueLocation: 'None · Whole site',
      sufficientlyMeasured: 'Yes — empty is not “safe”',
      material30DayStory: 'No qualifying pattern observed',
      topActionOwner: 'Review measurement coverage · safety leader',
      controlResultOrWhyUnavailable: 'No selected control',
    },
  }),
  fixture({
    id: 'blocked',
    label: 'Blocked — setup',
    roleHint: 'site_admin',
    nounCandidate: 'safety_issue',
    attentionCount: 0,
    now: {
      state: 'blocked',
      asOf: '—',
      topIssue: 'Unavailable',
      location: 'Whole site',
      detail: 'Finish site setup before analytical views.',
    },
    overTime: {
      state: 'blocked',
      asOf: '—',
      topIssue: 'Unavailable',
      location: 'Whole site',
      detail: 'Blocked until setup completes.',
    },
    sinceControl: {
      state: 'blocked',
      asOf: '—',
      topIssue: 'Unavailable',
      location: 'Whole site',
      detail: 'Blocked until setup completes.',
    },
    comprehension: {
      displayStateAndAsOf: 'Blocked · no as-of',
      topIssueLocation: 'Unavailable · Whole site',
      sufficientlyMeasured: 'No — setup incomplete',
      material30DayStory: 'Unavailable',
      topActionOwner: 'Open site setup · site admin + Edgentik support',
      controlResultOrWhyUnavailable: 'Blocked until setup',
    },
  }),
  fixture({
    id: 'unauthorized',
    label: 'Unauthorized',
    roleHint: 'portfolio_viewer',
    nounCandidate: 'safety_issue',
    attentionCount: 0,
    now: {
      state: 'unauthorized',
      asOf: '—',
      topIssue: 'Hidden',
      location: 'Hidden',
      detail: 'Counts must not leak inaccessible site detail.',
    },
    overTime: {
      state: 'unauthorized',
      asOf: '—',
      topIssue: 'Hidden',
      location: 'Hidden',
      detail: 'No cross-site benchmarking in A.',
    },
    sinceControl: {
      state: 'unauthorized',
      asOf: '—',
      topIssue: 'Hidden',
      location: 'Hidden',
      detail: 'No inaccessible detail via URLs or stubs.',
    },
    comprehension: {
      displayStateAndAsOf: 'Unauthorized',
      topIssueLocation: 'Hidden',
      sufficientlyMeasured: 'Unknown — no access',
      material30DayStory: 'Hidden',
      topActionOwner: 'Request access',
      controlResultOrWhyUnavailable: 'Unauthorized',
    },
  }),
  fixture({
    id: 'failure',
    label: 'API / projection failure',
    roleHint: 'safety_leader',
    nounCandidate: 'safety_issue',
    attentionCount: 0,
    now: {
      state: 'failure',
      asOf: '—',
      topIssue: 'Unavailable',
      location: 'Whole site',
      detail: 'No optimistic cached claim.',
    },
    overTime: {
      state: 'unavailable',
      asOf: '—',
      topIssue: 'Unavailable',
      location: 'Whole site',
      detail: 'Retry without inventing history.',
    },
    sinceControl: {
      state: 'failure',
      asOf: '—',
      topIssue: 'Unavailable',
      location: 'Whole site',
      detail: 'Retry / contact support.',
    },
    comprehension: {
      displayStateAndAsOf: 'Unavailable · no as-of',
      topIssueLocation: 'Unavailable · Whole site',
      sufficientlyMeasured: 'Unknown — temporary failure',
      material30DayStory: 'Unavailable',
      topActionOwner: 'Retry / contact support',
      controlResultOrWhyUnavailable: 'Temporarily unavailable',
    },
  }),
];

export function getFixtureById(id: string): PrototypeFixture | undefined {
  return PROTOTYPE_FIXTURES.find((f) => f.id === id);
}

export const COMPREHENSION_QUESTIONS = [
  'Current display state and as-of time?',
  'Top issue / location?',
  'Is the site sufficiently measured?',
  'One material 30-day story?',
  'Top required action / owner?',
  'Selected control descriptive result or why unavailable?',
] as const;
